import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, PlayCircle, AlertTriangle, Sparkles, Wallet, ShieldCheck, Link2, Lock, Copy, ChevronLeft, ChevronRight, ChevronDown, FileText, MailCheck } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Country, State } from "country-state-city";
import { groupCountries } from "@/data/countryOrder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cp_onboarding_v2_token";
const BG_IMG = "https://images.unsplash.com/photo-1673526759327-54f1f5b27322?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const api = (token) => axios.create({
  baseURL: API,
  headers: token ? { "X-Onboarding-Token": token } : {},
});

const glassCard = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const ctaBtn = "rounded-full border-0 bg-gradient-to-r from-violet-500 to-blue-500 text-white font-semibold hover:from-violet-400 hover:to-blue-400 shadow-[0_0_24px_rgba(139,92,246,0.35)] transition-colors";
const ghostBtn = "rounded-full border border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white transition-colors";
const darkInput = "bg-white/5 border-white/20 text-white placeholder:text-white/35 focus-visible:ring-violet-500 focus-visible:ring-offset-0";
const darkTrigger = "bg-white/5 border-white/20 text-white data-[placeholder]:text-white/40 focus:ring-violet-500 focus:ring-offset-0";
const darkMenu = "bg-[#0d1226] text-white border-white/10";
const darkItem = "focus:bg-white/10 focus:text-white";

function Backdrop() {
  return (
    <div className="fixed inset-0 z-0 pointer-events-none bg-[#02040a]">
      <img src={BG_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen" />
      <div className="absolute -top-40 -left-32 w-[34rem] h-[34rem] rounded-full bg-violet-600/25 blur-[120px]" />
      <div className="absolute bottom-[-10rem] right-[-8rem] w-[30rem] h-[30rem] rounded-full bg-blue-600/25 blur-[120px]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#02040a]/60 via-transparent to-[#02040a]" />
    </div>
  );
}

// AspectFit measures its own box and sizes an inner child to fill it while
// honouring `ratio`. Unlike CSS `aspect-ratio` combined with max-w/max-h
// (which only clamps one axis), this gives us a snug rectangle: the child
// grows to the largest possible size that still fits inside the parent on
// both axes. Used to make the focused video/slide "big" without cropping.
function AspectFit({ children, ratio = 16 / 9, className = "", style = {}, testId, dataAttrs = {} }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState(null);
  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      if (w / h > ratio) {
        setSize({ width: `${Math.floor(h * ratio)}px`, height: `${Math.floor(h)}px` });
      } else {
        setSize({ width: `${Math.floor(w)}px`, height: `${Math.floor(w / ratio)}px` });
      }
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, [ratio]);
  return (
    <div ref={wrapRef} className="w-full h-full min-h-0 min-w-0 flex items-center justify-center">
      <div style={{ ...(size || { width: "100%", aspectRatio: `${ratio}` }), ...style }} className={className} data-testid={testId} {...dataAttrs}>
        {children}
      </div>
    </div>
  );
}

function YouTubeEmbed({ id, fill = false }) {
  if (!id) return null;
  const iframe = (
    <iframe
      src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`}
      title="Onboarding video"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      className="w-full h-full"
    />
  );
  const wrapCls = "rounded-2xl overflow-hidden border border-violet-400/20 bg-black shadow-[0_0_40px_rgba(139,92,246,0.22)]";
  if (fill) {
    return <AspectFit ratio={16 / 9} className={wrapCls} testId="video-embed">{iframe}</AspectFit>;
  }
  return (
    <div className={`w-full ${wrapCls}`} style={{ aspectRatio: "16/9" }} data-testid="video-embed">
      {iframe}
    </div>
  );
}

function SelfHostedVideo({ src, onEnded, fill = false }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  // Max time (in seconds) the trainee has ACTUALLY watched. We use this to
  // block forward-seeking: the trainee is free to scrub BACK to review any
  // moment, but can never jump ahead past the point they've naturally reached
  // via playback. `controls` remains on so the browser's built-in kebab menu
  // still exposes playback rate (1.25x / 1.5x / 2x).
  const maxWatchedRef = useRef(0);
  const endedFiredRef = useRef(false);
  useEffect(() => {
    // Reset ceiling whenever the source changes (moving between steps mounts a
    // fresh <video>, but we might also swap src on the same component).
    maxWatchedRef.current = 0;
    endedFiredRef.current = false;
  }, [src]);
  const fireEnded = () => {
    if (endedFiredRef.current) return;
    endedFiredRef.current = true;
    onEnded && onEnded();
  };
  const handleTimeUpdate = (e) => {
    const el = e.currentTarget;
    const t = el.currentTime || 0;
    if (t > maxWatchedRef.current) maxWatchedRef.current = t;
    // Fallback: some browsers occasionally skip the native `ended` event
    // (esp. muted / after a scrub). Treat reaching the final ~0.4s as done so
    // the Continue/Next gate always unlocks once the trainee finishes.
    const dur = el.duration || 0;
    if (dur && t >= dur - 0.4) fireEnded();
  };
  const clampSeek = (el) => {
    if (el.currentTime > maxWatchedRef.current + 0.5) {
      el.currentTime = maxWatchedRef.current;
    }
  };
  const handleSeeking = (e) => clampSeek(e.currentTarget);
  // Some browsers debounce the `seeking` event when the video isn't fully
  // loaded — belt-and-suspenders: `seeked` fires after the browser actually
  // completes the seek, so we re-check the boundary here.
  const handleSeeked = (e) => clampSeek(e.currentTarget);
  const video = (
    <video
      ref={videoRef}
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload noremoteplayback"
      disablePictureInPicture
      onContextMenu={(e) => e.preventDefault()}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onEnded={() => { setPlaying(false); fireEnded(); }}
      onTimeUpdate={handleTimeUpdate}
      onSeeking={handleSeeking}
      onSeeked={handleSeeked}
      className="w-full h-full"
      src={src}
    />
  );
  const wrapCls = `rounded-2xl overflow-hidden border border-violet-400/20 bg-black shadow-[0_0_40px_rgba(139,92,246,0.22)] transition-transform duration-500 will-change-transform ${playing ? "scale-[1.015] shadow-[0_15px_70px_rgba(139,92,246,0.55)] relative z-10" : ""}`;
  if (fill) {
    return (
      <AspectFit
        ratio={16 / 9}
        className={wrapCls}
        testId="video-embed-native"
        dataAttrs={{ "data-playing": playing ? "1" : "0" }}
      >
        {video}
      </AspectFit>
    );
  }
  return (
    <div className={`w-full ${wrapCls}`} style={{ aspectRatio: "16/9" }} data-testid="video-embed-native" data-playing={playing ? "1" : "0"}>
      {video}
    </div>
  );
}

function CopyRow({ item, testId }) {
  const copyIt = async () => {
    try {
      await navigator.clipboard.writeText(item.value);
      toast.success(`${item.label} copied`);
    } catch {
      toast.error("Browser blocked clipboard access — long-press / right-click to copy");
    }
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{item.label}</div>
        {/^https?:\/\//i.test(item.value || "") ? (
          <a href={item.value} target="_blank" rel="noopener noreferrer"
            className="block text-xs font-mono text-sky-400 underline underline-offset-2 hover:text-sky-300 truncate"
            title={`${item.value} — tap to open`} data-testid={`${testId}-link`}>
            {item.value}
          </a>
        ) : (
          <div className="text-xs font-mono text-slate-200 truncate" title={item.value}>{item.value}</div>
        )}
      </div>
      <Button size="sm" className={`h-7 px-2.5 text-xs flex-shrink-0 ${ghostBtn}`} onClick={copyIt} data-testid={`${testId}-btn`}>
        <Copy size={12} className="mr-1" /> Copy
      </Button>
    </div>
  );
}

function StepVideo({ step, token, onEnded, fill = false }) {
  if (step.video_url) {
    return <SelfHostedVideo src={`${process.env.REACT_APP_BACKEND_URL}${step.video_url}?t=${encodeURIComponent(token || "")}`} onEnded={onEnded} fill={fill} />;
  }
  if (step.video_youtube_id) return <YouTubeEmbed id={step.video_youtube_id} fill={fill} />;
  return null;
}

// Additional documentation surfaced under each onboarding video. Fetches once
// per (slot, token) — the trainee token authenticates against the same backend
// endpoint that guards video playback.
// Additional documentation — inline slide viewer with next/prev navigation.
// The backend converts the PPTX to JPEG slides on first request and caches them
// in object storage; every trainee thereafter just fetches JPEGs one at a time.
// Additional documentation — inline slide viewer with next/prev navigation.
// The backend converts the PPTX to JPEG slides on first request and caches them
// in object storage; every trainee thereafter just fetches JPEGs one at a time.
function useStepDocs(slot, token) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  // manifest per doc.id: { count, renderable, display_name }
  const [manifests, setManifests] = useState({});
  // current slide index per doc.id (1-based)
  const [slideIdx, setSlideIdx] = useState({});

  const authParams = () => (token ? { t: token } : {});
  const authHeaders = () => (token ? undefined : { Authorization: `Bearer ${localStorage.getItem("cp_token") || ""}` });

  // Track which decks this trainee has actually opened (deduped per session).
  const trackedRef = useRef(new Set());
  const trackDocOpen = (docId) => {
    if (!token || !docId || trackedRef.current.has(docId)) return;
    trackedRef.current.add(docId);
    axios.post(`${API}/onboarding-v2/track`, { kind: "doc", doc_id: docId },
      { headers: { "X-Onboarding-Token": token } }).catch(() => {});
  };

  useEffect(() => {
    if (!slot) return;
    let alive = true;
    trackedRef.current = new Set();
    setLoading(true);
    axios.get(`${API}/videos/docs/for-slot/${slot}`, { params: authParams(), headers: authHeaders() })
      .then(async (r) => {
        if (!alive) return;
        const list = r.data?.docs || [];
        setDocs(list);
        const results = await Promise.all(list.map((d) =>
          axios.get(`${API}/videos/docs/${d.id}/slides`, { params: authParams(), headers: authHeaders() })
            .then((rr) => [d.id, rr.data])
            .catch(() => [d.id, { count: 0, renderable: false }])
        ));
        if (!alive) return;
        setManifests(Object.fromEntries(results));
        setSlideIdx(Object.fromEntries(results.map(([id, m]) => [id, m?.count ? 1 : 0])));
      })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slot, token]);

  const kb = (n) => n >= 1024 * 1024
    ? `${(n / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

  const downloadDoc = async (doc) => {
    setDownloadingId(doc.id);
    trackDocOpen(doc.id);
    try {
      const r = await axios.get(`${API}/videos/docs/${doc.id}/download`, {
        params: authParams(), headers: authHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const stem = (doc.display_name || (doc.filename || "document").replace(/\.[^.]+$/, ""));
      a.download = `${stem}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download document — try again in a moment.");
    } finally { setDownloadingId(null); }
  };

  const slideUrl = (docId, n) => {
    const q = token ? `?t=${encodeURIComponent(token)}` : `?auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}`;
    return `${API}/videos/docs/${docId}/slide/${n}${q}`;
  };

  const stepSlide = (docId, dir) => {
    const m = manifests[docId];
    if (!m?.count) return;
    trackDocOpen(docId);
    setSlideIdx((prev) => {
      const cur = prev[docId] || 1;
      const next = Math.max(1, Math.min(m.count, cur + dir));
      return { ...prev, [docId]: next };
    });
  };

  const hasRenderable = docs.some((d) => manifests[d.id]?.renderable && manifests[d.id]?.count > 0);

  return { docs, manifests, slideIdx, setSlideIdx, downloadDoc, downloadingId, slideUrl, stepSlide, kb, loading, hasRenderable, trackDocOpen };
}

// Big slide viewer — used when the user opens "View slides". Takes the full
// left column of SectionCard and offers a per-deck tab strip when the step has
// more than one renderable deck. The old "Next" button was removed (it clashed
// visually with the app-level Next button on the wizard nav bar); trainees now
// advance by clicking the slide itself.
function BigSlideViewer({ hook, testId }) {
  const { docs, manifests, slideIdx, stepSlide, slideUrl, downloadDoc, downloadingId, kb, trackDocOpen } = hook;
  const renderableDocs = docs.filter((d) => manifests[d.id]?.renderable && manifests[d.id]?.count > 0);
  const [activeDocId, setActiveDocId] = useState(() => renderableDocs[0]?.id || null);
  // If the deck list changes (new step / new slot), snap to the first renderable
  useEffect(() => {
    if (!activeDocId || !renderableDocs.some((d) => d.id === activeDocId)) {
      setActiveDocId(renderableDocs[0]?.id || null);
    }
  }, [renderableDocs.map((d) => d.id).join("|")]);
  // Viewing a deck in the big viewer counts as "opened" for analytics.
  useEffect(() => {
    if (activeDocId && trackDocOpen) trackDocOpen(activeDocId);
  }, [activeDocId]);
  if (!renderableDocs.length) return null;
  const active = renderableDocs.find((d) => d.id === activeDocId) || renderableDocs[0];
  const m = manifests[active.id];
  const cur = slideIdx[active.id] || 1;
  const atLast = cur >= m.count;
  const atFirst = cur <= 1;

  return (
    <div className="w-full h-full flex flex-col rounded-2xl border border-violet-400/20 bg-black overflow-hidden shadow-[0_0_40px_rgba(139,92,246,0.22)]" data-testid={`${testId}-viewer`}>
      {renderableDocs.length > 1 && (
        <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 border-b border-white/10 bg-white/[0.03] overflow-x-auto">
          {renderableDocs.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setActiveDocId(d.id)}
              className={`text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap transition ${d.id === active.id ? "bg-violet-500/25 text-white border border-violet-400/40" : "text-slate-400 hover:text-white hover:bg-white/[0.06] border border-transparent"}`}
              data-testid={`${testId}-tab-${d.id}`}
            >
              {d.display_name}
            </button>
          ))}
        </div>
      )}
      {/* Clickable slide — tap advances to the next slide (mimics a real
          presentation) so there's no in-viewer "Next" button that could be
          mistaken for the wizard's Next-step button. */}
      <button
        type="button"
        onClick={() => stepSlide(active.id, 1)}
        disabled={atLast}
        className={`flex-1 min-h-0 bg-black flex items-center justify-center relative group ${atLast ? "cursor-default" : "cursor-pointer"}`}
        data-testid={`${testId}-advance-${active.id}`}
        aria-label={atLast ? "Last slide" : `Advance to slide ${cur + 1}`}
      >
        <img
          key={`${active.id}-${cur}`}
          src={slideUrl(active.id, cur)}
          alt={`Slide ${cur} of ${m.count} — ${active.display_name}`}
          className="max-w-full max-h-full w-auto h-auto object-contain"
          loading="eager"
          data-testid={`${testId}-slide-img-${active.id}`}
          draggable={false}
        />
        {!atLast && (
          <span className="pointer-events-none absolute bottom-3 right-3 px-2 py-1 rounded-md bg-black/60 border border-white/10 text-[10px] font-mono text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity">
            Tap slide to advance
          </span>
        )}
      </button>
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-t border-white/10 bg-white/[0.03]">
        <Button
          size="sm"
          className={`h-8 px-3 text-xs ${ghostBtn}`}
          onClick={() => stepSlide(active.id, -1)}
          disabled={atFirst}
          data-testid={`${testId}-prev-${active.id}`}
          title="Go back one slide"
        >
          <ChevronLeft size={14} className="mr-1" /> Previous slide
        </Button>
        <div className="flex items-center gap-3">
          <div className="text-xs font-mono text-slate-300" data-testid={`${testId}-counter-${active.id}`}>
            Slide <span className="text-white font-semibold">{cur}</span> of {m.count}
            <span className="ml-2 text-slate-500 hidden sm:inline">· {active.display_name}</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[11px] text-slate-500 hover:text-white"
            onClick={() => downloadDoc(active)}
            disabled={downloadingId === active.id}
            data-testid={`${testId}-download-${active.id}`}
            title={`Download ${active.display_name} (PDF)`}
          >
            {downloadingId === active.id ? <Loader2 size={11} className="animate-spin mr-1" /> : <ArrowRight size={11} className="mr-1 rotate-90" />}
            Download
          </Button>
        </div>
        {/* Placeholder to keep the counter visually centered — no in-viewer
            "Next" button (see comment on the clickable image above). */}
        <span aria-hidden className="w-[104px] hidden sm:block" />
      </div>
    </div>
  );
}

function StepDocs({ slot, token, testId, embedded = false }) {
  const hook = useStepDocs(slot, token);
  const { docs, manifests, slideIdx, downloadDoc, downloadingId, slideUrl, stepSlide, kb, loading } = hook;

  if (loading) return null;
  if (!docs.length) return null;

  const list = (
    <ul className="space-y-4">
        {docs.map((d) => {
          const m = manifests[d.id];
          const cur = slideIdx[d.id] || 1;
          const canView = m?.renderable && m?.count > 0;
          return (
            <li key={d.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3" data-testid={`${testId}-item-${d.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{d.display_name}</div>
                  {d.blurb ? <div className="text-xs text-slate-400 mt-0.5">{d.blurb}</div> : null}
                  <div className="text-[11px] text-slate-500 mt-1 uppercase tracking-wide">
                    PDF · {kb(d.size || 0)}
                    {canView ? <span className="ml-2 text-violet-300/70 normal-case tracking-normal">· {m.count} slides</span> : null}
                  </div>
                </div>
                <Button
                  size="sm"
                  className={`h-8 px-3 text-xs flex-shrink-0 ${ghostBtn}`}
                  onClick={() => downloadDoc(d)}
                  disabled={downloadingId === d.id}
                  data-testid={`${testId}-download-${d.id}`}
                  title="Download this deck"
                >
                  {downloadingId === d.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <ArrowRight size={12} className="mr-1 rotate-90" />}
                  Download
                </Button>
              </div>

              {canView ? (
                <div className="rounded-lg overflow-hidden bg-black/40 border border-white/[0.06]" data-testid={`${testId}-slides-${d.id}`}>
                  <div className="aspect-[16/10] bg-black flex items-center justify-center">
                    <img
                      key={cur}
                      src={slideUrl(d.id, cur)}
                      alt={`Slide ${cur} of ${m.count}`}
                      className="w-full h-full object-contain"
                      loading="eager"
                      data-testid={`${testId}-slide-img-${d.id}`}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white/[0.04]">
                    <Button
                      size="sm"
                      className={`h-8 px-3 text-xs ${ghostBtn}`}
                      onClick={() => stepSlide(d.id, -1)}
                      disabled={cur <= 1}
                      data-testid={`${testId}-prev-${d.id}`}
                    >
                      <ArrowRight size={12} className="mr-1 rotate-180" /> Previous
                    </Button>
                    <div className="text-xs font-mono text-slate-300" data-testid={`${testId}-counter-${d.id}`}>
                      Slide <span className="text-white font-semibold">{cur}</span> of {m.count}
                    </div>
                    <Button
                      size="sm"
                      className={`h-8 px-3 text-xs ${ghostBtn}`}
                      onClick={() => stepSlide(d.id, 1)}
                      disabled={cur >= m.count}
                      data-testid={`${testId}-next-${d.id}`}
                    >
                      Next <ArrowRight size={12} className="ml-1" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
  );

  if (embedded) return <div data-testid={testId}>{list}</div>;

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-md bg-violet-500/15 border border-violet-400/30 flex items-center justify-center">
          <Link2 size={12} className="text-violet-300" />
        </div>
        <h5 className="text-xs font-bold uppercase tracking-wider text-violet-200/90">View slides</h5>
        <span className="text-[11px] text-slate-500">· The following slides will guide you through the key points covered in the video.</span>
      </div>
      {list}
    </div>
  );
}

function LocationPicker({ pendingLocation, setPendingLocation, onSave, loading }) {
  const countries = useMemo(() => Country.getAllCountries(), []);
  const countryGroups = useMemo(() => groupCountries(countries), [countries]);
  const selectedCountry = useMemo(
    () => countries.find((c) => c.name === pendingLocation.country) || null,
    [countries, pendingLocation.country]
  );
  const states = useMemo(
    () => (selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : []),
    [selectedCountry]
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
      <div>
        <Label className="text-white/80 font-semibold text-sm">Country</Label>
        <Select
          value={pendingLocation.country}
          onValueChange={(v) => setPendingLocation({ country: v, state: "" })}
        >
          <SelectTrigger data-testid="loc-country" className={`mt-1 w-full ${darkTrigger}`}>
            <SelectValue placeholder="Select a country" />
          </SelectTrigger>
          <SelectContent className={`${darkMenu} max-h-72`}>
            {countryGroups.map((g) => (
              <SelectGroup key={g.label}>
                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.label}</SelectLabel>
                {g.items.map((c) => (
                  <SelectItem key={c.isoCode} value={c.name} className={darkItem} data-testid={`loc-country-${c.isoCode}`}>{c.name}</SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-white/80 font-semibold text-sm">State / Province</Label>
        {states.length > 0 ? (
          <Select
            value={pendingLocation.state}
            onValueChange={(v) => setPendingLocation({ ...pendingLocation, state: v })}
            disabled={!selectedCountry}
          >
            <SelectTrigger data-testid="loc-state" className={`mt-1 w-full ${darkTrigger}`}>
              <SelectValue placeholder="Select a state / province" />
            </SelectTrigger>
            <SelectContent className={`${darkMenu} max-h-72`}>
              {states.map((s) => (
                <SelectItem key={s.isoCode} value={s.name} className={darkItem}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            data-testid="loc-state"
            value={pendingLocation.state}
            onChange={(e) => setPendingLocation({ ...pendingLocation, state: e.target.value })}
            placeholder={selectedCountry ? "Type your region" : "Select a country first"}
            disabled={!selectedCountry}
            className={`mt-1 ${darkInput}`}
          />
        )}
      </div>
      <div className="sm:col-span-2">
        <Button size="lg" className={`w-full mt-2 ${ctaBtn}`} data-testid="loc-save" onClick={onSave} disabled={loading}>Save and continue</Button>
      </div>
    </div>
  );
}

export default function OnboardingV2() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [content, setContent] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(null); // { email } after signup, before email verified
  const [verifying, setVerifying] = useState(false);
  const [signupForm, setSignupForm] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    // Prefill from the account they just created on the login page (if any) so
    // they don't re-type their name / email / phone. URL params win over the
    // stored copy when both are present.
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem("cp_onboard_prefill") || "{}"); } catch { /* ignore */ }
    return {
      name: p.get("name") || stored.name || "",
      phone: p.get("phone") || stored.phone || "",
      email: p.get("email") || stored.email || "",
    };
  });
  const [pendingLocation, setPendingLocation] = useState({ country: "", state: "" });
  const [carypactUrl, setCarypactUrl] = useState("");
  const sponsorSlug = params.get("ref") || params.get("inviteCode") || null;
  const sponsorSuffix = params.get("s") || params.get("suffix") || null;
  const inviteToken = params.get("i") || null;
  // Marketing campaign key — sponsor is resolved from it server-side.
  const utmKey = params.get("k") || null;
  // Client-side navigation overrides so the trainee can always press Previous.
  // - `stageOverride` forces the section rendered (used to go back from the
  //   submit card to Section B, or from Section B step 1 back to Section A).
  // - `stageInitialIdx` seeds the target SectionCard on which step to open.
  // - `preIdxOverride` lets a trainee scroll back through completed pre-steps
  //   without touching the server-side counter.
  const [stageOverride, setStageOverride] = useState(null);
  const [stageInitialIdx, setStageInitialIdx] = useState(null);
  const [preIdxOverride, setPreIdxOverride] = useState(null);
  // Intro pre-step video watched-gate — held in React state (not just
  // localStorage) so finishing the video re-renders and unlocks Continue.
  const [introWatched, setIntroWatched] = useState(() => {
    try { return !!(JSON.parse(localStorage.getItem("cp_ob_watched") || "{}").intro); } catch { return false; }
  });

  // Register an emailed-invite click on landing — the person shows in Prospects
  // (identified by the email we sent to) even before they create an account.
  useEffect(() => {
    if (!inviteToken) return;
    axios.post(`${API}/discover/land`, { session_id: `inv-${inviteToken}`.slice(0, 80), ref: sponsorSlug || null, suffix: sponsorSuffix || null, invite_token: inviteToken }).catch(() => {});
  }, [inviteToken, sponsorSlug, sponsorSuffix]);

  // Load static content once (public).
  // Load content (public; with the session token the sponsor invite link is included).
  useEffect(() => {
    axios.get(`${API}/onboarding-v2/content`, { headers: token ? { "X-Onboarding-Token": token } : {} })
      .then((r) => setContent(r.data)).catch(() => {});
  }, [token]);

  // Load progress if we have a token.
  const refreshMe = async (tkn) => {
    if (!tkn) return;
    try {
      const { data } = await api(tkn).get("/onboarding-v2/me");
      setMe(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setMe(null);
    }
  };
  useEffect(() => { if (token) refreshMe(token); }, [token]);

  // Email-verification link handler: opening {origin}/onboarding-v2?verify=<token>
  // consumes the token, unlocks the session and continues the flow.
  const verifyTokenParam = params.get("verify");
  useEffect(() => {
    if (!verifyTokenParam) return;
    (async () => {
      setVerifying(true);
      try {
        const { data } = await axios.post(`${API}/onboarding-v2/verify-email`, { token: verifyTokenParam });
        if (data.token) {
          localStorage.setItem(TOKEN_KEY, data.token);
          setToken(data.token);
          setPendingVerify(null);
          toast.success("Email verified — let's go!");
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "That verification link is invalid or has expired.");
      } finally {
        setVerifying(false);
        const sp = new URLSearchParams(window.location.search);
        sp.delete("verify");
        nav(`/onboarding-v2${sp.toString() ? `?${sp.toString()}` : ""}`, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyTokenParam]);

  const resendVerify = async () => {
    if (!pendingVerify?.email) return;
    try {
      await axios.post(`${API}/onboarding-v2/resend-verification`, { email: pendingVerify.email, origin: window.location.origin });
      toast.success("Verification email re-sent — check your inbox.");
    } catch { toast.error("Could not resend right now. Try again shortly."); }
  };

  // Fire-and-forget engagement tracking (only for real trainees with a token).
  // Powers cross-device "skip when rewatching" + the admin analytics badges.
  const trackAsset = (payload) => {
    if (!token) return;
    api(token).post("/onboarding-v2/track", payload).catch(() => {});
  };
  // Once the server progress arrives, honour a video the trainee already
  // finished on any device so they're never re-gated on the intro.
  useEffect(() => {
    if (me?.videos_watched?.includes("intro")) setIntroWatched(true);
  }, [me?.videos_watched]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!signupForm.name.trim() || !signupForm.email.trim() || !signupForm.phone.trim()) {
      toast.error("Name, phone and email are required"); return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/onboarding-v2/start`, {
        name: signupForm.name.trim(), email: signupForm.email.trim(), phone: signupForm.phone.trim(), sponsor_slug: sponsorSlug, sponsor_suffix: sponsorSuffix,
        session_id: localStorage.getItem("discover_sid") || null, origin: window.location.origin,
        utm_key: utmKey || localStorage.getItem("discover_utm") || null,
      });
      // Prefill has served its purpose — clear it so a later visitor on this
      // browser never inherits someone else's details.
      try { localStorage.removeItem("cp_onboard_prefill"); } catch { /* ignore */ }
      if (data.requires_verification) {
        setPendingVerify({ email: data.email || signupForm.email.trim() });
        toast.success("Almost there — check your email to verify and continue.");
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(data.resumed ? "Welcome back — picking up where you left off" : "Let's go!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start onboarding");
    } finally { setLoading(false); }
  };

  const advance = async () => {
    setLoading(true);
    try {
      await api(token).post("/onboarding-v2/pre-step/advance");
      setPreIdxOverride(null);
      await refreshMe(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot advance");
    } finally { setLoading(false); }
  };

  const setReady = async (ready) => {
    setLoading(true);
    try {
      const { data } = await api(token).post("/onboarding-v2/pre-step/ready", { ready });
      if (data.opted_out) {
        toast.success("No problem — we've let your admin know. Come back anytime.");
        await refreshMe(token);
      } else {
        await advance();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const saveLocation = async () => {
    if (!pendingLocation.country || !pendingLocation.state) {
      toast.error("Country and state are required"); return;
    }
    setLoading(true);
    try {
      await api(token).post("/onboarding-v2/pre-step/location", pendingLocation);
      await advance();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setLoading(false); }
  };

  const completeStep = async (section, step) => {
    setLoading(true);
    try {
      await api(token).post("/onboarding-v2/section-step/complete", { section, step });
      // Any real progress clears the manual stage/step overrides so the
      // computed stage takes over again.
      setStageOverride(null);
      setStageInitialIdx(null);
      await refreshMe(token);
      toast.success(`Section ${section} · Step ${step} complete`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!carypactUrl.trim()) { toast.error("Paste your CaryPact link first"); return; }
    setLoading(true);
    try {
      await api(token).post("/onboarding-v2/submit", { carypact_url: carypactUrl.trim() });
      await refreshMe(token);
      toast.success("Submitted! Waiting for admin approval.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally { setLoading(false); }
  };

  // ---------- computed state ----------
  const serverPreIdx = me?.progress?.pre_step_index ?? 0;
  // If the trainee has clicked Previous on the pre-steps we show that older
  // page but keep the underlying server pointer intact.
  const preStepIndex = preIdxOverride ?? serverPreIdx;
  const preStepsDone = content ? serverPreIdx >= content.pre_steps.length : false;
  const doneA = new Set(me?.progress?.section_a_completed || []);
  const doneB = new Set(me?.progress?.section_b_completed || []);
  const sectionAComplete = doneA.size === 4;
  const sectionBComplete = doneB.size === 4 && sectionAComplete;
  const testMode = !!me?.test_mode;

  const totalSteps = content ? (content.pre_steps.length + 4 + 4 + 1) : 16;
  const stepsDoneCount = useMemo(() => {
    if (!content) return 0;
    return Math.min(serverPreIdx, content.pre_steps.length) + doneA.size + doneB.size + (me?.submitted ? 1 : 0);
  }, [content, serverPreIdx, doneA.size, doneB.size, me?.submitted]);
  const percent = Math.round((stepsDoneCount / totalSteps) * 100);

  // Pre-step navigation helpers.
  const preAdvanceSmart = async () => {
    // If we're viewing an already-committed pre-step (via override), just
    // move the override forward — don't blindly hit the API and skip ahead
    // of the server's counter.
    if (preIdxOverride !== null && preIdxOverride < serverPreIdx) {
      setPreIdxOverride(preIdxOverride + 1);
      return;
    }
    await advance();
  };
  const preGoBack = () => {
    if (preStepIndex > 0) setPreIdxOverride(preStepIndex - 1);
  };

  // ---------- render ----------
  if (!content) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        <Backdrop />
        <Loader2 className="animate-spin text-violet-300 relative z-10" />
      </div>
    );
  }

  // Verifying an email link.
  if (verifying) {
    return (
      <div className="min-h-screen relative flex items-center justify-center text-white" data-testid="ob-verifying">
        <Backdrop />
        <div className="relative z-10 text-center">
          <Loader2 className="animate-spin text-violet-300 mx-auto mb-3" size={30} />
          <p className="text-slate-300">Verifying your email…</p>
        </div>
      </div>
    );
  }

  // Signed up, email not verified yet — hard gate: check your inbox.
  if (pendingVerify && !token) {
    return (
      <div className="min-h-screen relative text-white flex items-center justify-center px-4 py-10" data-testid="ob-verify-pending">
        <Backdrop />
        <div className={`relative z-10 max-w-md w-full p-8 text-center ob-rise ${glassCard}`}>
          <div className="w-16 h-16 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200 flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(139,92,246,0.25)]"><MailCheck size={30} /></div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2 text-white">Verify your email to continue</h2>
          <p className="text-slate-400">We sent a verification link to <b className="text-slate-200">{pendingVerify.email}</b>. Click it to unlock your onboarding — the link expires in 48 hours.</p>
          <div className="mt-6 space-y-2.5">
            <Button size="lg" data-testid="verify-continue-btn" disabled={loading} onClick={() => handleStart({ preventDefault: () => {} })} className={`w-full ${ctaBtn}`}>
              {loading ? <Loader2 className="animate-spin" /> : <>I&apos;ve verified — continue <ArrowRight size={16} className="ml-1" /></>}
            </Button>
            <Button size="sm" variant="ghost" data-testid="verify-resend-btn" onClick={resendVerify} className={ghostBtn}>
              Resend verification email
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-5">Wrong address?{" "}
            <button className="text-violet-300 underline" data-testid="verify-change-email" onClick={() => setPendingVerify(null)}>Start again</button>
          </p>
        </div>
      </div>
    );
  }

  // Not started yet — signup screen.
  if (!token || !me) {
    return (
      <div className="min-h-screen relative text-white flex items-center justify-center px-4 py-10">
        <Backdrop />
        <div className={`relative z-10 max-w-lg w-full p-8 sm:p-10 ob-rise ${glassCard}`}>
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200 text-xs font-semibold mb-5">
              <Sparkles size={12} /> One-time onboarding · about 10 min
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
              Welcome to <span className="text-grad-cp">BotChainCP</span>
            </h1>
            <p className="text-slate-400 mt-3">We&apos;ll walk you through setting up your CaryPact wallet, funding it, buying your first hashrate, and staking. Enter your details to begin.</p>
          </div>
          <form onSubmit={handleStart} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white/80 font-semibold text-sm">Your full name</Label>
              <Input id="name" data-testid="signup-name" value={signupForm.name} onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} required
                     className={`mt-1 ${darkInput}`} />
            </div>
            <div>
              <Label htmlFor="phone" className="text-white/80 font-semibold text-sm">Phone number</Label>
              <Input id="phone" data-testid="signup-phone" type="tel" value={signupForm.phone} onChange={(e) => setSignupForm({ ...signupForm, phone: e.target.value })} placeholder="e.g. +1 555 123 4567" required
                     className={`mt-1 ${darkInput}`} />
            </div>
            <div>
              <Label htmlFor="email" className="text-white/80 font-semibold text-sm">Email</Label>
              <Input id="email" data-testid="signup-email" type="email" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} placeholder="you@example.com" required
                     className={`mt-1 ${darkInput}`} />
              {/^.+@test\.botchaincp\.com$/i.test(signupForm.email.trim()) && (
                <p className="text-[11px] text-emerald-300 mt-1" data-testid="signup-test-email-note">
                  Test address — no email verification, and you can run this flow again with it any time.
                </p>
              )}
            </div>
            <Button type="submit" size="lg" data-testid="signup-submit" disabled={loading} className={`w-full ${ctaBtn}`}>
              {loading ? <Loader2 className="animate-spin" /> : <>Begin onboarding <ArrowRight size={16} className="ml-1" /></>}
            </Button>
          </form>
          <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
            {[{ i: Wallet, t: "Set up wallet" }, { i: Link2, t: "Bind to sponsor" }, { i: ShieldCheck, t: "Start earning" }].map(({ i: Ic, t }) => (
              <div key={t} className="text-[11px] text-slate-400 flex flex-col items-center gap-1.5">
                <span className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-violet-300"><Ic size={14} /></span>
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Opted out — show final message.
  if (me.opted_out) {
    return (
      <div className="min-h-screen relative text-white flex items-center justify-center px-4">
        <Backdrop />
        <div className={`relative z-10 max-w-md w-full p-8 text-center ob-rise ${glassCard}`}>
          <div className="w-14 h-14 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 flex items-center justify-center mx-auto mb-4"><AlertTriangle size={26} /></div>
          <h2 className="font-display text-2xl font-bold mb-2 text-white">We&apos;ll be here when you&apos;re ready</h2>
          <p className="text-slate-400">Your admin has been notified. Come back anytime — just visit this same page with your email and we&apos;ll pick up where you paused.</p>
          <p className="text-xs text-slate-500 mt-4">Signed in as <b className="text-slate-300">{me.email}</b></p>
          <Button size="sm" className={`mt-4 ${ghostBtn}`} data-testid="start-over-btn" onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setMe(null); }}>
            Start over as a different person
          </Button>
        </div>
      </div>
    );
  }

  // Submitted → waiting for admin approval.
  if (me.submitted) {
    return (
      <div className="min-h-screen relative text-white flex items-center justify-center px-4">
        <Backdrop />
        <div className={`relative z-10 max-w-md w-full p-8 text-center ob-rise ${glassCard}`} data-testid="submitted-card">
          <div className="w-16 h-16 rounded-full bg-emerald-400/15 border border-emerald-400/30 text-emerald-300 flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(52,211,153,0.25)]"><CheckCircle2 size={30} /></div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2 text-grad-cp">Congratulations!</h2>
          <p className="text-slate-400 mb-4">You&apos;ve completed your one-time onboarding. Your admin will review your submission and email you a login as soon as it&apos;s approved.</p>
          <p className="text-xs text-slate-500">You can close this window. We&apos;ll email <b className="text-slate-300">{me.email}</b> when it&apos;s ready.</p>
          <Button size="sm" className={`mt-5 ${ghostBtn}`} data-testid="start-over-btn" onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setMe(null); }}>
            Start over as a different person
          </Button>
        </div>
      </div>
    );
  }

  // Pre-steps in progress.
  if (!preStepsDone) {
    const step = content.pre_steps[preStepIndex];
    const isIntroStep = step.id === "intro-video";
    const hasVideo = !!(step.video_url || step.video_youtube_id);
    // Videos are SKIPPABLE — Continue is never blocked. We still record who
    // actually finished the intro (feeds the video-engagement report).
    const introNotWatched = isIntroStep && hasVideo && !introWatched;
    const markIntroWatched = () => {
      setIntroWatched(true);
      trackAsset({ kind: "video", slot: "intro" });
      try {
        const w = JSON.parse(localStorage.getItem("cp_ob_watched") || "{}");
        w.intro = true;
        localStorage.setItem("cp_ob_watched", JSON.stringify(w));
      } catch { /* localStorage unavailable — non-fatal */ }
    };
    const canPreBack = preStepIndex > 0;
    // If the step has a video (currently only intro), lay the card out as a
    // full-height flex column so the video fills the middle and the nav row
    // stays pinned to the bottom — no scroll, no clipped buttons.
    if (hasVideo || (isIntroStep && !hasVideo)) {
      return (
        <WizardShell percent={percent} title={`Pre-step ${preStepIndex + 1} of ${content.pre_steps.length}`}>
          <div className={`flex-1 min-h-0 p-5 md:p-6 flex flex-col ob-rise ${glassCard}`} data-testid={`pre-step-card-${step.id}`}>
            <div className="flex-shrink-0 mb-3">
              <h2 className="font-display text-xl md:text-2xl font-bold text-white" data-testid={`pre-step-title-${step.id}`}>{step.title}</h2>
              <p className="text-slate-400 text-sm mt-1 whitespace-pre-line">{step.body}</p>
            </div>

            <div className="flex-1 min-h-0 flex flex-col min-w-0">
              {hasVideo ? (
                <StepVideo step={step} token={token} onEnded={isIntroStep ? markIntroWatched : undefined} fill />
              ) : (
                <div className="flex-1 min-h-0 flex items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/[0.03]" data-testid="intro-video-placeholder">
                  <div className="text-center">
                    <PlayCircle className="mx-auto text-violet-300/60 mb-2" size={28} />
                    <p className="text-sm text-slate-400">Welcome video coming soon — continue to the next step.</p>
                  </div>
                </div>
              )}
            </div>

            {isIntroStep && hasVideo && (
              <div className="flex-shrink-0 mt-3">
                <StepDocs slot="intro" token={token} testId="step-docs-intro" />
              </div>
            )}

            {/* Previous / Continue row — pinned to the bottom of the viewport */}
            <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-3">
              <Button
                size="lg"
                variant="ghost"
                className={ghostBtn + " disabled:opacity-30"}
                onClick={preGoBack}
                disabled={!canPreBack || loading}
                data-testid={`pre-step-prev-${step.id}`}
              >
                <ChevronLeft size={16} className="mr-1" /> Previous
              </Button>
              <div className="flex flex-col items-end">
                <Button
                  size="lg"
                  className={ctaBtn}
                  data-testid={`advance-${step.id}`}
                  onClick={preAdvanceSmart}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin" /> : (step.cta || "Continue")} <ArrowRight size={16} className="ml-1" />
                </Button>
                {introNotWatched && (
                  <p className="mt-1 text-[11px] text-slate-400 font-mono flex items-center gap-1" data-testid="intro-skip-hint">
                    <PlayCircle size={12} /> You can skip ahead — the video stays here if you want it later
                  </p>
                )}
              </div>
            </div>
          </div>
        </WizardShell>
      );
    }
    // Plain / text-only pre-steps (welcome, pre-summary, disclaimer, ready,
    // location) keep the compact centered card — no video means no height
    // pressure.
    return (
      <WizardShell percent={percent} title={`Pre-step ${preStepIndex + 1} of ${content.pre_steps.length}`}>
        <div className={`p-8 ob-rise ${glassCard}`} data-testid={`pre-step-card-${step.id}`}>
          <h2 className="font-display text-2xl font-bold mb-3 text-white" data-testid={`pre-step-title-${step.id}`}>{step.title}</h2>
          <p className="text-slate-400 mb-4 whitespace-pre-line">{step.body}</p>

          {step.id === "ready" && (
            <div className="grid gap-3 mt-4">
              <Button size="lg" className={ctaBtn} data-testid="ready-yes" onClick={() => setReady(true)} disabled={loading}>I am ready — let&apos;s go</Button>
              <Button size="lg" className={ghostBtn} data-testid="ready-no" onClick={() => setReady(false)} disabled={loading}>Not right now — email my admin</Button>
            </div>
          )}

          {step.id === "location" && (
            <LocationPicker pendingLocation={pendingLocation} setPendingLocation={setPendingLocation} onSave={saveLocation} loading={loading} />
          )}

          {/* Previous / Next row — Previous always present (disabled on first
              page); Next only on plain click-through steps. */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <Button
              size="lg"
              variant="ghost"
              className={ghostBtn + " disabled:opacity-30"}
              onClick={preGoBack}
              disabled={!canPreBack || loading}
              data-testid={`pre-step-prev-${step.id}`}
            >
              <ChevronLeft size={16} className="mr-1" /> Previous
            </Button>
            {!["ready", "location"].includes(step.id) && (
              <Button
                size="lg"
                className={ctaBtn}
                data-testid={`advance-${step.id}`}
                onClick={preAdvanceSmart}
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" /> : (step.cta || "Continue")} <ArrowRight size={16} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </WizardShell>
    );
  }

  // Section A / B / Submit. Only ONE of these is rendered at any moment — the
  // page-flip animation handles the transition between them so the trainee
  // always sees a single-viewport experience with no scroll.
  const autoStage = !sectionAComplete ? "A" : !sectionBComplete ? "B" : "submit";
  const stage = stageOverride || autoStage;
  const initialIdx = stageOverride ? stageInitialIdx : null;
  // When the user hits Previous on the first step of Section B, jump back to
  // the last step of Section A (only reachable when A is complete, so we can
  // land safely on step 4).
  const goBackToA = () => { setStageOverride("A"); setStageInitialIdx(content.section_a.length - 1); };
  // When the user hits Next on the last (already-done) step of Section A, we
  // let the user progress into Section B — this only happens when they've
  // navigated back into A via the Previous button above (unfinished A ends
  // via `completeStep` → autoStage transition).
  const goForwardFromA = () => { setStageOverride(null); setStageInitialIdx(null); };
  // Back from Section B step 1 → last Section A step (see above).
  const goForwardFromB = () => { setStageOverride(null); setStageInitialIdx(null); };
  // Submit → Section B last step.
  const goBackToSubmitPrev = () => { setStageOverride("B"); setStageInitialIdx(content.section_b.length - 1); };
  return (
    <WizardShell percent={percent} title="CaryPact wallet setup & first transactions">
      {stage === "A" && (
        <SectionCard
          letter="A"
          title="Wallet Setup"
          steps={content.section_a}
          done={doneA}
          onComplete={(n) => completeStep("A", n)}
          disabled={loading}
          token={token}
          testMode={testMode}
          initialIdx={initialIdx}
          serverWatched={me?.videos_watched || []}
          onVideoWatched={(slot) => trackAsset({ kind: "video", slot })}
          onNextBeyondLast={sectionAComplete ? goForwardFromA : null}
        />
      )}
      {stage === "B" && (
        <SectionCard
          letter="B"
          title="First Transactions"
          steps={content.section_b}
          done={doneB}
          onComplete={(n) => completeStep("B", n)}
          disabled={loading}
          token={token}
          testMode={testMode}
          initialIdx={initialIdx}
          serverWatched={me?.videos_watched || []}
          onVideoWatched={(slot) => trackAsset({ kind: "video", slot })}
          onPrevBeyondFirst={goBackToA}
          onNextBeyondLast={sectionBComplete ? goForwardFromB : null}
        />
      )}
      {stage === "submit" && !me.submitted && (
        <div className={`flex-1 min-h-0 p-8 flex flex-col justify-center ob-flip-fwd ${glassCard}`} data-testid="submit-card">
          <h2 className="font-display text-2xl font-bold mb-2 text-white">🎉 Almost there — one last thing</h2>
          <p className="text-slate-400 mb-4">Paste <b className="text-white">your own CaryPact invite link</b> below — in the CaryPact app it&apos;s your personal share/invite link. It identifies your wallet so we can match you to your team.</p>
          <p className="text-xs text-amber-300/90 mb-2">Careful: paste <b>your</b> link — not the invite your sponsor sent you.</p>
          <p className="text-xs text-slate-500 mb-2">The link must be from <code className="px-1 rounded bg-white/10 text-slate-300">app.carypact.com</code> and include both <code className="px-1 rounded bg-white/10 text-slate-300">inviteCode</code> and <code className="px-1 rounded bg-white/10 text-slate-300">suffix</code>.</p>
          <div
            className="relative mb-3"
            onPaste={(e) => {
              e.preventDefault();
              const t = (e.clipboardData || window.clipboardData).getData("text");
              setCarypactUrl(t.trim());
            }}
          >
            <Input
              data-testid="carypact-url"
              value={carypactUrl}
              readOnly
              placeholder="Right-click here and paste your CaryPact URL…"
              className={`pr-24 cursor-text ${darkInput}`}
              onKeyDown={(e) => {
                // allow paste shortcut (ctrl/cmd + v) but block manual typing
                const isPasteCombo = (e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V");
                if (!isPasteCombo) e.preventDefault();
              }}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              <Button size="sm" className={`h-7 px-3 text-xs ${ghostBtn}`} data-testid="paste-carypact-url"
                      onClick={async () => {
                        try {
                          const txt = await navigator.clipboard.readText();
                          if (txt) setCarypactUrl(txt.trim());
                          else toast.error("Clipboard is empty");
                        } catch { toast.error("Browser blocked clipboard access — right-click → Paste"); }
                      }}>
                Paste
              </Button>
              {carypactUrl && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-400 hover:text-white hover:bg-white/10" data-testid="clear-carypact-url" onClick={() => setCarypactUrl("")}>
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="lg"
              variant="ghost"
              className={`${ghostBtn} shrink-0`}
              onClick={goBackToSubmitPrev}
              disabled={loading}
              data-testid="submit-prev"
              title="Go back to the previous step"
            >
              <ChevronLeft size={16} className="mr-1" /> Previous
            </Button>
            <Button size="lg" className={`flex-1 ${ctaBtn}`} data-testid="submit-onboarding" onClick={handleSubmit} disabled={loading || !carypactUrl}>
              {loading ? <Loader2 className="animate-spin" /> : "Submit for admin approval"}
            </Button>
          </div>
        </div>
      )}
    </WizardShell>
  );
}

function WizardShell({ children, percent, title }) {
  return (
    <div className="h-screen flex flex-col relative text-white overflow-hidden">
      <Backdrop />
      <div className="flex-shrink-0 z-20 bg-[#02040a]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[100rem] mx-auto px-4 py-2.5 flex items-center gap-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 flex-shrink-0 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-violet-400 to-blue-400 shadow-[0_0_8px_rgba(139,92,246,0.8)]" />
            <span className="hidden sm:inline">CaryPact Onboarding</span>
          </div>
          <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-blue-400 transition-[width] duration-500" style={{ width: `${percent}%` }} />
          </div>
          <div className="text-xs font-bold w-10 text-right text-grad-cp">{percent}%</div>
        </div>
      </div>
      <div className="relative z-10 flex-1 min-h-0 max-w-[100rem] w-full mx-auto px-4 py-3 flex flex-col">
        {title && <p className="text-[10px] uppercase tracking-widest text-slate-500 flex-shrink-0">{title}</p>}
        <div className="flex-1 min-h-0 mt-2 flex flex-col">{children}</div>
      </div>
    </div>
  );
}

function SectionCard({ letter, title, steps, done, onComplete, disabled, token, testMode = false, initialIdx = null, onPrevBeyondFirst = null, onNextBeyondLast = null, serverWatched = [], onVideoWatched = null }) {
  const [watched, setWatched] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cp_ob_watched") || "{}"); } catch { return {}; }
  });
  const markWatched = (key) => {
    if (onVideoWatched) onVideoWatched(key.toLowerCase());
    setWatched((w) => {
      const n = { ...w, [key]: true };
      localStorage.setItem("cp_ob_watched", JSON.stringify(n));
      return n;
    });
  };
  const serverWatchedSet = React.useMemo(() => new Set(serverWatched), [serverWatched.join("|")]);

  const firstIncomplete = steps.findIndex((s) => !done.has(s.n));
  // `initialIdx` (when provided by the parent) wins — this lets us land on the
  // last step of the section when the trainee navigated back into it from a
  // later stage (submit → B, or B → A).
  const [idx, setIdx] = useState(() => {
    if (typeof initialIdx === "number") return Math.max(0, Math.min(steps.length - 1, initialIdx));
    return firstIncomplete === -1 ? steps.length - 1 : firstIncomplete;
  });
  const [docsOpen, setDocsOpen] = useState(false);
  const [flipDir, setFlipDir] = useState("fwd");

  useEffect(() => {
    // Don't auto-jump if the parent seeded a specific step (they're viewing
    // a completed step on purpose).
    if (typeof initialIdx === "number") return;
    const next = steps.findIndex((s) => !done.has(s.n));
    if (next === -1) setIdx(steps.length - 1);
    else if (next > idx) setIdx(next);
  }, [done.size]);

  // Auto-close the docs viewer when moving to a new step so the trainee sees
  // the video first on every new page.
  useEffect(() => { setDocsOpen(false); }, [idx]);

  const s = steps[idx];
  const docsHook = useStepDocs(s ? `${letter.toLowerCase()}${s.n}` : null, token);
  if (!s) return null;
  const watchKey = `${letter}${s.n}`;
  const videoSlot = `${letter.toLowerCase()}${s.n}`;
  const isDone = done.has(s.n);
  // Videos are SKIPPABLE — Next is never blocked. We still track who actually
  // finished each video (locally + server-side) for the engagement report.
  const notWatchedYet = !isDone && !!s.video_url && !watched[watchKey] && !serverWatchedSet.has(videoSlot);
  const hasSlides = docsHook.hasRenderable;
  const atFirst = idx === 0;
  const atLast = idx === steps.length - 1;
  const canPrev = !atFirst || !!onPrevBeyondFirst;

  const goPrev = () => {
    if (atFirst) {
      if (onPrevBeyondFirst) { setFlipDir("back"); onPrevBeyondFirst(); }
      return;
    }
    setFlipDir("back");
    setIdx(idx - 1);
  };
  const goNext = async () => {
    setFlipDir("fwd");
    if (!isDone) {
      await onComplete(s.n);
      if (idx < steps.length - 1) setIdx(idx + 1);
      // If this was the last step, parent's autoStage flips to the next
      // section (submit / B) automatically on the next re-render.
    } else if (atLast) {
      if (onNextBeyondLast) onNextBeyondLast();
    } else {
      setIdx((i) => Math.min(steps.length - 1, i + 1));
    }
  };

  const testId = `step-docs-${letter}${s.n}`;

  return (
    <div className={`flex-1 min-h-0 flex flex-col ${glassCard} p-4 sm:p-5`} data-testid={`section-${letter}`}>
      {/* Section header — one compact row */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 text-white text-xs font-bold shadow-[0_0_16px_rgba(139,92,246,0.5)] flex-shrink-0">{letter}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-display text-base font-bold text-white leading-tight">{title}</h3>
            <span className="text-[11px] text-slate-500 font-mono">Step {idx + 1} of {steps.length}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5" role="tablist">
          {steps.map((st, i) => {
            const stDone = done.has(st.n);
            const stActive = i === idx;
            const stReachable = stDone || i <= (firstIncomplete === -1 ? steps.length - 1 : firstIncomplete);
            return (
              <button
                key={st.n}
                type="button"
                role="tab"
                aria-selected={stActive}
                onClick={() => {
                  if (!stReachable) return;
                  setFlipDir(i > idx ? "fwd" : "back");
                  setIdx(i);
                }}
                disabled={!stReachable}
                className={`h-2 rounded-full transition-all ${stActive ? "w-6 bg-gradient-to-r from-violet-400 to-blue-400 shadow-[0_0_10px_rgba(139,92,246,0.8)]" : stDone ? "w-2 bg-emerald-400/80" : stReachable ? "w-2 bg-white/25 hover:bg-white/40" : "w-2 bg-white/8"}`}
                data-testid={`section-${letter}-dot-${st.n}`}
                title={`${letter}${st.n} — ${st.title}${stDone ? " (done)" : ""}`}
              />
            );
          })}
        </div>
      </div>

      {/* Page (book-flip animated on every idx change) */}
      <div
        key={`${letter}-${idx}-${docsOpen ? "docs" : "vid"}`}
        className={`flex-1 min-h-0 grid md:grid-cols-[minmax(0,1fr)_300px] gap-4 md:gap-5 ${flipDir === "fwd" ? "ob-flip-fwd" : "ob-flip-back"}`}
        data-testid={`section-${letter}-step-${s.n}`}
        data-mode={docsOpen ? "docs" : "video"}
      >
        {docsOpen && hasSlides ? (
          <>
            {/* Docs mode — BIG slides left, compact info + mini video right */}
            <div className="flex flex-col min-h-0 min-w-0">
              <BigSlideViewer hook={docsHook} testId={testId} />
            </div>
            <div className="flex flex-col min-h-0 min-w-0 gap-3 overflow-y-auto pr-1">
              <div className="flex-shrink-0">
                <div className="flex items-baseline gap-2">
                  <span className={`inline-flex items-center justify-center flex-shrink-0 w-7 h-7 rounded-full text-xs font-bold ${isDone ? "bg-emerald-400 text-[#02040a]" : "bg-white/10 text-slate-200 border border-white/10"}`}>
                    {isDone ? <CheckCircle2 size={15} /> : s.n}
                  </span>
                  <h4 className="font-display font-semibold text-white text-base leading-tight">{s.title}</h4>
                </div>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{s.body}</p>
              </div>

              {/* Small video thumbnail so trainee doesn't lose the video */}
              {(s.video_youtube_id || s.video_url) && (
                <div className="flex-shrink-0" data-testid={`section-${letter}-step-${s.n}-video-mini`}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Step video</div>
                  <StepVideo step={s} token={token} onEnded={() => markWatched(watchKey)} />
                </div>
              )}

              {s.copy_items?.length ? (
                <div className="flex-shrink-0 space-y-1.5" data-testid={`copy-items-${letter}-${s.n}`}>
                  {s.copy_items.map((c, i) => (
                    <CopyRow key={i} item={c} testId={`copy-item-${letter}${s.n}-${i}`} />
                  ))}
                </div>
              ) : null}

              <button
                type="button"
                onClick={() => setDocsOpen(false)}
                className="flex-shrink-0 inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-slate-300 hover:text-white text-xs font-semibold transition"
                data-testid={`${testId}-close`}
              >
                <PlayCircle size={13} /> Back to full video
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Video mode (default) — BIG video left (fills box), info + docs toggle right */}
            <div className="flex flex-col min-h-0 min-w-0">
              {(s.video_youtube_id || s.video_url) ? (
                <StepVideo step={s} token={token} onEnded={() => markWatched(watchKey)} fill />
              ) : (
                <div className="flex-1 min-h-0 flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02]">
                  <span className="text-slate-500 text-sm">No video for this step</span>
                </div>
              )}
            </div>
            <div className="flex flex-col min-h-0 min-w-0 gap-3 overflow-y-auto pr-1">
              <div className="flex-shrink-0">
                <div className="flex items-baseline gap-2">
                  <span className={`inline-flex items-center justify-center flex-shrink-0 w-7 h-7 rounded-full text-xs font-bold ${isDone ? "bg-emerald-400 text-[#02040a]" : "bg-white/10 text-slate-200 border border-white/10"}`}>
                    {isDone ? <CheckCircle2 size={15} /> : s.n}
                  </span>
                  <h4 className="font-display font-semibold text-white text-lg leading-tight">{s.title}</h4>
                </div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.body}</p>
                {isDone && (
                  <span className="mt-2 inline-flex items-center gap-1.5 text-emerald-300 text-xs font-medium" data-testid={`section-${letter}-step-${s.n}-completed`}>
                    <CheckCircle2 size={12} /> Step already completed
                  </span>
                )}
              </div>

              {s.copy_items?.length ? (
                <div className="flex-shrink-0 space-y-1.5" data-testid={`copy-items-${letter}-${s.n}`}>
                  {s.copy_items.map((c, i) => (
                    <CopyRow key={i} item={c} testId={`copy-item-${letter}${s.n}-${i}`} />
                  ))}
                </div>
              ) : null}

              {/* View slides button — only when at least one deck is renderable */}
              {hasSlides && (
                <button
                  type="button"
                  onClick={() => setDocsOpen(true)}
                  className="flex-shrink-0 inline-flex items-center justify-between gap-3 px-3 py-3 rounded-xl border border-violet-400/30 bg-violet-500/[0.08] hover:bg-violet-500/[0.14] hover:border-violet-400/50 text-left transition group"
                  data-testid={`${testId}-toggle`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-500/25 border border-violet-400/30 text-violet-200 flex-shrink-0">
                      <FileText size={14} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs font-bold uppercase tracking-wider text-violet-200/95">View slides</span>
                      <span className="block text-[11px] text-slate-400 mt-0.5">
                        The following slides will guide you through the key points covered in the video.
                      </span>
                    </span>
                  </span>
                  <ChevronRight size={16} className="text-violet-300 flex-shrink-0 transition group-hover:translate-x-0.5" />
                </button>
              )}

              {s.notes?.length ? (
                <ul className="mt-auto text-[11px] text-slate-500 space-y-0.5 flex-shrink-0">
                  {s.notes.map((n, i) => <li key={i} className="flex items-start gap-1"><span className="text-slate-600">·</span><span>{n}</span></li>)}
                </ul>
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* Big prev/next nav bar */}
      <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={goPrev}
          disabled={!canPrev || disabled}
          className="group inline-flex items-center gap-2 h-11 pl-2.5 pr-4 rounded-full border border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.07] hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/[0.03]"
          data-testid={`section-${letter}-prev`}
          aria-label="Previous step"
        >
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/10 group-hover:bg-white/15 transition-colors">
            <ChevronLeft size={16} />
          </span>
          <span className="text-sm font-semibold hidden sm:inline">Previous</span>
        </button>

        <div className="text-[11px] font-mono text-slate-500 hidden sm:block">
          {notWatchedYet ? (
            <span className="inline-flex items-center gap-1" data-testid={`section-${letter}-skip-hint`}>
              <PlayCircle size={12} /> Skip any time — Next always works
            </span>
          ) : (
            <span>{idx + 1} / {steps.length}</span>
          )}
        </div>

        <button
          type="button"
          onClick={goNext}
          disabled={disabled}
          className="group inline-flex items-center gap-2 h-11 pr-2.5 pl-4 rounded-full transition bg-gradient-to-r from-violet-500 to-blue-500 text-white shadow-[0_10px_30px_rgba(139,92,246,0.35)] hover:shadow-[0_15px_40px_rgba(139,92,246,0.55)] hover:scale-[1.02]"
          data-testid={`section-${letter}-next`}
          aria-label="Next step"
        >
          <span className="text-sm font-semibold hidden sm:inline">{isDone ? (idx < steps.length - 1 ? "Next" : "Done") : "Mark done & Next"}</span>
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/20 group-hover:bg-white/30 transition-colors">
            {disabled ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
          </span>
        </button>
      </div>
    </div>
  );
}
